// =====================================================
// 树形视图组件
// 参考 Apple 产品层级设计
// =====================================================

import React, { useState, useCallback } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Package,
  Boxes,
  Cpu,
  Monitor,
  Tag,
} from 'lucide-react';
import type { TreeNode, ProductCategory } from '../../types/hierarchy';

// =====================================================
// 类型定义
// =====================================================

interface TreeViewProps {
  data: TreeNode[];
  selectedId?: string;
  expandedNodes: Set<string>;
  onSelect: (node: TreeNode) => void;
  onToggle: (nodeId: string) => void;
  isLoading?: boolean;
}

interface TreeNodeComponentProps {
  node: TreeNode;
  depth: number;
  selectedId?: string;
  expandedNodes: Set<string>;
  onSelect: (node: TreeNode) => void;
  onToggle: (nodeId: string) => void;
}

// =====================================================
// 图标组件
// =====================================================

function getNodeIcon(node: TreeNode, isExpanded: boolean) {
  const iconClass = "w-4 h-4";
  
  switch (node.type) {
    case 'category':
      const category = node.data?.category as ProductCategory;
      if (category === 'all_in_one') {
        return <Monitor className={`${iconClass} text-purple-500`} />;
      } else if (category === 'product') {
        return <Package className={`${iconClass} text-blue-500`} />;
      } else {
        return <Cpu className={`${iconClass} text-orange-500`} />;
      }
    case 'series':
      return isExpanded 
        ? <FolderOpen className={`${iconClass} text-yellow-500`} />
        : <Folder className={`${iconClass} text-yellow-500`} />;
    case 'family':
      return <Boxes className={`${iconClass} text-green-500`} />;
    case 'model':
      return <Package className={`${iconClass} text-blue-400`} />;
    case 'sku':
      return <Tag className={`${iconClass} text-gray-400`} />;
    default:
      return <Folder className={iconClass} />;
  }
}

// =====================================================
// 树节点组件
// =====================================================

function TreeNodeComponent({
  node,
  depth,
  selectedId,
  expandedNodes,
  onSelect,
  onToggle,
}: TreeNodeComponentProps) {
  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedId === node.id;
  const hasChildren = node.children && node.children.length > 0;

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(node.id);
  }, [node.id, onToggle]);

  const handleSelect = useCallback(() => {
    onSelect(node);
    // 选择时也展开
    if (hasChildren && !isExpanded) {
      onToggle(node.id);
    }
  }, [node, onSelect, hasChildren, isExpanded, onToggle]);

  return (
    <div className="select-none">
      {/* 节点行 */}
      <div
        className={`
          flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer
          transition-colors duration-150
          ${isSelected 
            ? 'bg-blue-50 text-blue-700' 
            : 'hover:bg-gray-50 text-gray-700'
          }
        `}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleSelect}
      >
        {/* 展开/收起按钮 */}
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          {hasChildren ? (
            <button
              onClick={handleToggle}
              className="p-0.5 hover:bg-gray-200 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </button>
          ) : (
            <span className="w-4" /> // 占位
          )}
        </div>

        {/* 图标 */}
        <div className="flex-shrink-0">
          {getNodeIcon(node, isExpanded)}
        </div>

        {/* 名称 */}
        <span className={`truncate flex-1 text-sm ${isSelected ? 'font-medium' : ''}`}>
          {node.name}
        </span>

        {/* 子项数量 */}
        {hasChildren && (
          <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
            isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
          }`}>
            {node.children!.length}
          </span>
        )}

        {/* Pitch 标签 */}
        {node.data?.pitch && (
          <span className="text-xs text-gray-400 flex-shrink-0">
            P{node.data.pitch.toFixed(2)}
          </span>
        )}
      </div>

      {/* 子节点 */}
      {isExpanded && hasChildren && (
        <div className="relative">
          {/* 连接线 */}
          <div
            className="absolute left-0 top-0 bottom-0 w-px bg-gray-200"
            style={{ marginLeft: `${depth * 16 + 18}px` }}
          />
          {node.children!.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedNodes={expandedNodes}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================
// 主组件
// =====================================================

export function TreeView({
  data,
  selectedId,
  expandedNodes,
  onSelect,
  onToggle,
  isLoading,
}: TreeViewProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        暂无数据
      </div>
    );
  }

  return (
    <div className="py-2">
      {data.map((node) => (
        <TreeNodeComponent
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          expandedNodes={expandedNodes}
          onSelect={onSelect}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

// =====================================================
// 快捷操作栏
// =====================================================

interface TreeActionsProps {
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onRefresh: () => void;
}

export function TreeActions({ onExpandAll, onCollapseAll, onRefresh }: TreeActionsProps) {
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
      <button
        onClick={onExpandAll}
        className="px-2 py-1 hover:bg-gray-100 rounded transition-colors"
      >
        全部展开
      </button>
      <span className="text-gray-300">|</span>
      <button
        onClick={onCollapseAll}
        className="px-2 py-1 hover:bg-gray-100 rounded transition-colors"
      >
        全部收起
      </button>
      <span className="text-gray-300">|</span>
      <button
        onClick={onRefresh}
        className="px-2 py-1 hover:bg-gray-100 rounded transition-colors"
      >
        刷新
      </button>
    </div>
  );
}

export default TreeView;
